/**
 * SuggestionChips Component
 *
 * Context-aware quick action buttons based on the current page.
 * Shown when conversation is empty.
 * Phase 7: Frontend Integration
 * Phase 16: Enhanced onboarding with capability categories
 * Feature 4: Context-Aware Help via help-context.config
 *
 * @module SuggestionChips
 */

'use client';

import { Box, Flex, Text, stripLocale } from '@axon/ui';
import { useTranslations, useLocale } from 'next-intl';
import { usePathname } from 'next/navigation';
import { LuSparkles } from 'react-icons/lu';

import {
  getPageHelpConfig,
  getDefaultSuggestions,
  resolveSuggestion,
} from '@/config/help-context.config';

import { useChatbotContext } from '../providers/ChatbotProvider';

import { SuggestionChipRow } from './SuggestionChipRow';

// ============================================================================
// Component
// ============================================================================

/**
 * SuggestionChips component
 *
 * Displays context-aware suggestion chips based on the current page.
 * Falls back to default suggestions for unmapped pages.
 *
 * @example
 * {messages.length === 0 && <SuggestionChips />}
 */
export function SuggestionChips() {
  const { sendMessage, session } = useChatbotContext();
  const t = useTranslations('chatbot');
  const locale = useLocale();
  const pathname = usePathname();

  const isConnected = session?.isConnected ?? false;

  // Page-aware suggestions from help-context config
  const normalizedPath = stripLocale(pathname);
  const pageConfig = getPageHelpConfig(normalizedPath);

  const suggestions = pageConfig
    ? pageConfig.suggestions.map((key, i) => ({
        id: `page-${i}`,
        label: resolveSuggestion(key, locale),
        query: resolveSuggestion(key, locale),
      }))
    : getDefaultSuggestions(locale);

  const handleSuggestionClick = async (query: string) => {
    if (!isConnected) return;
    await sendMessage(query);
  };

  return (
    <Box
      paddingX="4"
      paddingY="3"
      borderTop="1px solid"
      borderColor="border"
      bg="bg.subtle"
      flexShrink={0}
      overflowY="auto"
      maxHeight="240px"
    >
      {/* Header */}
      <Flex align="center" gap="1" color="fg.muted" marginBottom="2">
        <LuSparkles size={14} />
        <Text fontSize="xs" fontWeight="medium">
          {t('suggestions.title')}
        </Text>
      </Flex>

      {/* Suggestion chips — starters show in full (cap=0); presentation is
          shared with message follow-ups via SuggestionChipRow. */}
      <SuggestionChipRow
        chips={suggestions.map((s) => ({ id: s.id, label: s.label, value: s.query }))}
        onSelect={(value) => void handleSuggestionClick(value)}
        disabled={!isConnected}
        cap={0}
      />
    </Box>
  );
}

export default SuggestionChips;
